/**
 * File attach — pick files into a form as closable chips, posted with the form
 *
 * Markup (inside a <form>, which must be multipart for a plain or htmx post):
 *
 *   <div data-file-attach>
 *       <div class="file-attachment-list" data-file-attach-list hidden></div>
 *       <input type="file" name="files" multiple hidden data-file-attach-input>
 *       <button type="button" class="chip chip-xs" data-file-attach-trigger>Attach</button>
 *   </div>
 *
 * The trigger opens the hidden input.  Each pick adds to the files already picked
 * (a native input would replace them), a file picked twice is kept once, and each
 * chip's X removes its file.  The picked files are written back to the input through
 * a DataTransfer, so the form posts them as ordinary multipart files: nothing uploads
 * before submit.  The list stays hidden while it is empty.  form.reset() clears it
 * (Houses and Loop call it after a successful post through their own data-success-reset);
 * a form that clears itself some other way has to call reset() too.  Every listener is delegated from document, so a
 * composer swapped in by htmx needs no init.
 *
 * Options:
 *   accept="image/*" (or "image/jpeg,image/png", ".pdf", ...) on the input — the
 *       picker offers only those, and a file that slips through anyway (the picker's
 *       "All files" choice) is dropped with an error toast.  accept="image/*" makes it images-only.  A file
 *       the browser reports no type for (e.g. .heic on some desktops) passes the type
 *       rules, since the picker already offered it.
 *   data-file-attach-max="10" on the root — at most this many files; extras are
 *       dropped with an error toast.
 *   data-file-attach-max-bytes="5242880" on the root — at most this many bytes in
 *       total; a new file that would go past it is dropped with an error toast.
 *   data-file-attach-thumbs on the root — an image file's chip shows a thumbnail in
 *       place of its file-type icon.
 *
 * Each change fires `fileattach:change` on the root (bubbles) with
 * detail.files, for page glue such as making a message optional once a file is attached.
 * The server still has to enforce its own limits.
 */

(function () {
    var picked = new WeakMap();
    var chipFile = new WeakMap();

    function toastError(message) {
        if (window.SeeMe && window.SeeMe.toast) window.SeeMe.toast.error(message);
    }

    // The accept attribute's rules: ".ext", "type/*" or an exact MIME type.  A file with no
    // reported type passes the MIME rules: the picker offered it, and the server checks it.
    function accepts(input, file) {
        var accept = (input.getAttribute('accept') || '').trim();
        if (!accept) return true;
        var name = file.name.toLowerCase();
        var type = file.type.toLowerCase();
        return accept.split(',').some(function (token) {
            token = token.trim().toLowerCase();
            if (!token) return false;
            if (token.charAt(0) === '.') return name.slice(-token.length) === token;
            if (!type) return true;
            if (token.slice(-2) === '/*') return type.indexOf(token.slice(0, -1)) === 0;
            return type === token;
        });
    }

    function formatSize(bytes) {
        if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return Math.max(1, Math.round(bytes / 1024)) + ' KB';
    }

    function iconFor(file) {
        var type = file.type;
        if (type.indexOf('image/') === 0) return 'fa-file-image';
        if (type === 'application/pdf') return 'fa-file-pdf';
        if (/word/.test(type)) return 'fa-file-word';
        if (/excel|spreadsheet|csv/.test(type)) return 'fa-file-excel';
        return 'fa-paperclip';
    }

    function sameFile(a, b) {
        return a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;
    }

    function buildChip(root, file) {
        var item = document.createElement('div');
        item.className = 'file-attachment';
        var icon;
        if (root.hasAttribute('data-file-attach-thumbs') && file.type.indexOf('image/') === 0) {
            icon = document.createElement('img');
            icon.className = 'file-uploader-thumb';
            icon.alt = '';
            // The image keeps showing once decoded, so the object URL is freed right away
            // and nothing leaks however the chip later leaves the page.
            icon.onload = icon.onerror = function () { URL.revokeObjectURL(icon.src); };
            icon.src = URL.createObjectURL(file);
        } else {
            icon = document.createElement('i');
            icon.className = 'fa-solid ' + iconFor(file);
        }
        var name = document.createElement('span');
        name.className = 'file-attachment__name';
        name.textContent = file.name;
        name.title = file.name;
        var size = document.createElement('span');
        size.className = 'file-attachment__size';
        size.textContent = formatSize(file.size);
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'file-attachment__remove';
        remove.setAttribute('aria-label', 'Remove ' + file.name);
        remove.setAttribute('data-file-attach-remove', '');
        remove.innerHTML = '<i class="fa-solid fa-xmark"></i>';
        item.append(icon, name, size, remove);
        chipFile.set(item, file);
        return item;
    }

    // Only the chips that changed are touched: a removed file's chip goes, a new file's
    // chip is appended, and the rest (thumbnails included) stay as they are.
    function render(root) {
        var files = picked.get(root) || [];
        var input = root.querySelector('[data-file-attach-input]');
        var list = root.querySelector('[data-file-attach-list]');
        var transfer = new DataTransfer();
        files.forEach(function (file) { transfer.items.add(file); });
        input.files = transfer.files;
        if (!list) return;

        list.setAttribute('aria-live', 'polite');
        var shown = [];
        Array.prototype.slice.call(list.children).forEach(function (chip) {
            var file = chipFile.get(chip);
            if (files.indexOf(file) === -1) {
                chip.remove();
            } else {
                shown.push(file);
            }
        });
        files.forEach(function (file) {
            if (shown.indexOf(file) === -1) list.appendChild(buildChip(root, file));
        });
        list.hidden = files.length === 0;
    }

    function update(root, files) {
        picked.set(root, files);
        render(root);
        root.dispatchEvent(new CustomEvent('fileattach:change', {bubbles: true, detail: {files: files.slice()}}));
    }

    document.addEventListener('click', function (event) {
        if (!event.target.closest) return;
        var trigger = event.target.closest('[data-file-attach-trigger]');
        var remove = event.target.closest('[data-file-attach-remove]');
        var control = trigger || remove;
        var root = control && control.closest('[data-file-attach]');
        if (!root) return;
        if (trigger) {
            root.querySelector('[data-file-attach-input]').click();
            return;
        }
        var chip = remove.closest('.file-attachment');
        var neighbor = chip.nextElementSibling || chip.previousElementSibling;
        var files = (picked.get(root) || []).filter(function (file) { return file !== chipFile.get(chip); });
        update(root, files);
        // The removed chip held focus, so move it to the next chip's X, else the previous
        // one's, else the trigger, instead of losing it to <body>.
        var next = (neighbor && neighbor.querySelector('[data-file-attach-remove]'))
            || root.querySelector('[data-file-attach-trigger]');
        if (next) next.focus();
    });

    document.addEventListener('change', function (event) {
        var input = event.target.closest && event.target.closest('[data-file-attach-input]');
        var root = input && input.closest('[data-file-attach]');
        if (!root) return;
        var files = (picked.get(root) || []).slice();
        var max = parseInt(root.getAttribute('data-file-attach-max'), 10);
        var maxBytes = parseInt(root.getAttribute('data-file-attach-max-bytes'), 10);
        var total = files.reduce(function (sum, file) { return sum + file.size; }, 0);
        var wrongType = false, overCount = false, overBytes = false;
        // Files already picked always stay; each new one is added only if it fits.
        Array.prototype.forEach.call(input.files, function (file) {
            if (files.some(function (kept) { return sameFile(kept, file); })) return;
            if (!accepts(input, file)) wrongType = true;
            else if (max && files.length >= max) overCount = true;
            else if (maxBytes && total + file.size > maxBytes) overBytes = true;
            else { files.push(file); total += file.size; }
        });
        if (wrongType) {
            var imagesOnly = input.getAttribute('accept').split(',').every(function (token) {
                return token.trim().toLowerCase().indexOf('image/') === 0;
            });
            toastError(imagesOnly ? 'Only images can be attached' : 'That type of file can\'t be attached here');
        }
        if (overCount) toastError('Attach at most ' + max + ' file' + (max === 1 ? '' : 's'));
        if (overBytes) toastError('Attachments can be at most ' + formatSize(maxBytes) + ' in total');
        update(root, files);
    });

    // Cleared after the reset has run, so a reset any listener cancels keeps the files, and
    // fileattach:change glue sees the form's reset values.  The root can be the form itself
    // or inside it.
    document.addEventListener('reset', function (event) {
        var form = event.target;
        if (!form.querySelectorAll) return;
        setTimeout(function () {
            if (event.defaultPrevented) return;
            var roots = Array.prototype.slice.call(form.querySelectorAll('[data-file-attach]'));
            if (form.matches('[data-file-attach]')) roots.push(form);
            roots.forEach(function (root) { update(root, []); });
        }, 0);
    });
})();
